import { Box, Stack, FormControl, FormLabel, Text } from '@chakra-ui/react';
import Drawer from 'components/presentational/Drawer';
import { Field } from 'formik';
import NameField from 'components/forms/new/formikFormFields/NameField';
import FieldToolTip from 'components/forms/new/FieldToolTip';
import {
    formLabelStyling,
    stackStyling,
    descriptionStyling,
} from 'components/forms/new/formStyles';
import FormBase from '@/components/forms/new/FormBase';
import { useState, useEffect, useMemo } from 'react';
import { axios } from '../../../services/axios';
import { useToast } from '@chakra-ui/react';
import { PARTNER } from 'pages/organisations/sharedComponents/BillingForm';

const FieldsDrawer = ({
    isOpen,
    onClose = () => {},
    data,
    state,
    mode,
    disabled = false,
    onChange,
    darkMode,
}) => {
    const isPartner = mode === PARTNER;
    const fieldList = useMemo(
        () =>
            isPartner
                ? ['pAttribute1', 'pAttribute2', 'pAttribute3']
                : ['attribute1', 'attribute2', 'attribute3'],
        [isPartner],
    );

    const [fields, setFields] = useState(() => {
        const initial = {};
        fieldList.forEach((v) => {
            initial[v] = state?.[v] ?? '';
        });
        return initial;
    });

    useEffect(() => {
        const initial = {};
        fieldList.forEach((v) => {
            initial[v] = state?.[v] ?? '';
        });
        setFields(initial);
    }, [isOpen, state, mode, fieldList]);

    const toast = useToast();

    const handleSubmit = async (values) => {
        const nonEmptyFields = {};
        fieldList.forEach((v) => {
            if (values[v] && values[v].trim() !== '') {
                nonEmptyFields[v] = values[v].trim();
            }
        });

        if (Object.keys(nonEmptyFields).length === 0) {
            onClose();
            return;
        }

        try {
            const invoiceId =
                data?.invoiceId || data?.id || state?.invoiceId || state?.id;
            const params = new URLSearchParams();
            Object.entries(nonEmptyFields).forEach(([k, v]) => {
                params.append(k, v);
            });
            await axios.put(
                `/invoice/attributes/${invoiceId}?${params.toString()}`,
            );
            toast({
                title: 'Custom Attributes Updated',
                status: 'success',
                duration: 2000,
                isClosable: true,
            });
            if (onChange) onChange();
            onClose();
        } catch (err) {
            toast({
                title: 'Update Failed',
                description: 'Failed to update custom attributes.',
                status: 'error',
                duration: 2000,
                isClosable: true,
            });
        }
    };

    const [hoveredField, setHoveredField] = useState(null);

    const formFields = ({ errors, touched }) => (
        <Stack w="100%" maxW="800px" mx="auto" pb="6">
            {fieldList.map((v, idx) => {
                return (
                    <FormControl
                        key={v}
                        sx={{ margin: '0 !important' }}
                        isInvalid={!!errors?.[v] && touched?.[v]}
                        onMouseEnter={() => setHoveredField(v)}
                        onMouseLeave={() => setHoveredField(null)}>
                        <Stack direction="row" sx={stackStyling(true)}>
                            <Box>
                                <FormLabel sx={formLabelStyling(darkMode)}>
                                    {`Custom Attribute ${idx + 1}`}
                                </FormLabel>
                                <Text sx={descriptionStyling} maxWidth={250}>
                                    Enter custom attribute for this invoice.
                                </Text>
                            </Box>
                            <Box flex="1" minWidth="250px">
                                <Box
                                    sx={{
                                        display: 'flex',
                                        width: '96%',
                                        alignItems: 'flex-start',
                                        paddingTop: '8px',
                                    }}>
                                    <Field
                                        component={NameField}
                                        id={v}
                                        name={v}
                                        isDisabled={disabled}
                                        error={errors?.[v]}
                                        darkMode={darkMode}
                                    />
                                    {hoveredField === v && (
                                        <FieldToolTip
                                            label="Enter custom attribute for this invoice."
                                            ariaLabel={`custom-attribute-tip-${idx}`}
                                            left="25px"
                                            top="25px"
                                        />
                                    )}
                                </Box>
                            </Box>
                        </Stack>
                    </FormControl>
                );
            })}
        </Stack>
    );

    return (
        <Drawer isOpen={isOpen} onClose={onClose}>
            <FormBase
                initialValues={fields}
                finalFormSubmit={handleSubmit}
                header={`Custom Attributes ${data?.invoiceNumber ? `for Invoice ${data.invoiceNumber}` : ''}`}
                backText="Cancel"
                customClick={true}
                onBack={onClose}>
                {(formProps) => formFields(formProps)}
            </FormBase>
        </Drawer>
    );
};

export default FieldsDrawer;
